<template>
  <div class="context-menu-bar-exports">
    <menubar>
      <template #leftItems>
        <b-dropdown toggle-class="mono" class="dropdown-default" data-cy="export-open-ddn">
          <template #button-content>
            <span>{{ vm.selectedExportProject?.name ?? exportProjects[0]?.name }}</span>
          </template>
          <b-dropdown-item
            :active="(vm.selectedExportProject ? vm.selectedExportProject?.exportId : exportProjects[0]?.exportId) === exportProject.exportId"
            @click="handleExportProjectSelection(exportProject)"
            v-for="exportProject in exportProjects"
            :key="exportProject.exportId"
            >{{ exportProject.name }}
          </b-dropdown-item>
        </b-dropdown>
        <span v-tooltip="$t('ManageProjects')">
          <b-button class="btn-icon mono" @click="openManageExport" :disabled="showManageExport">
            <i class="mdp-Open"></i>
          </b-button>
        </span>

        <b-button class="btn-icon mono" v-tooltip="$t('new')" @click="resetData" :disabled="isNew || showManageExport" data-cy="export-add-new-btn">
          <i class="mdp-Plus"></i>
        </b-button>
        <b-button
          class="btn-icon mono"
          v-tooltip="$t('delete')"
          @click="deleteExport"
          :disabled="isNew || isDeleteDisabled || showManageExport"
          data-cy="export-delete-btn">
          <i class="mdp-Delete"></i>
        </b-button>
      </template>
      <template #rightItems>
        <b-button class="btn-with-icon mono" @click="showTranslationModal()" v-if="exportType == 'FastPage'">
          <i class="mdp-Dictionary"></i>
          <span> {{ $t("translation.table") }}</span>
        </b-button>
        <MultiLevelDropDownButton
          v-if="exportType == 'FastPage'"
          :split="true"
          :level="1"
          data-cy="fastpage-layout-designer-ddn"
          :button-label="$t('layoutDesigner')"
          :icon="'mdp-ListAndLabel'"
          :items="vm.navMenuItems"
          @click="handleMenuItem">
        </MultiLevelDropDownButton>
        <b-button
          class="btn-with-icon mono"
          data-cy="last-export-btn"
          @click="goToLastExport()"
          :disabled="route.params.id === Guid.empty || !vm.hasPreviousExport">
          <span><i class="mdp-History"></i></span>
          <span>{{ $t("lastExport") }}</span>
        </b-button>
      </template>
    </menubar>
    <TranslationTableDialog v-model:show-translation-table="vm.showTranslationDialog"></TranslationTableDialog>
  </div>
</template>

<script setup lang="ts">
import { Guid } from "@/util/guid";
import { useToast } from "@common/composable/toast";
import { ExportProjectModel } from "@medapro-types/Exports/Exports";
import { ExportsActions, ExportsGetter, ExportsState } from "@medapro-types/Exports/store/exports";
import exportApi from "@services/exports";
import { AxiosError } from "axios";
import emitter from "tiny-emitter/instance";
import { computed, onMounted, reactive, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { createNamespacedHelpers } from "vuex-composition-helpers";
import menubar from "../../common/components/MenuBar/MenuBar.vue";
import { ExportType } from "@medapro-types/Exports/export-enum";
import TranslationTableDialog from "./FastPage/TranslationTableDialog.vue";
import MultiLevelDropDownButton from "@common/components/MultiLevelDropDownButton.vue";
import { NavMenuItem } from "@medapro-types/common/common";

//#region General
const { t: $t, te: $te } = useI18n();
const route = useRoute();
const router = useRouter();
const { createToast } = useToast();
//#endregion

const { useState: useExportState, useActions } = createNamespacedHelpers<ExportsState, ExportsGetter, ExportsActions>("exports");
const { exportProjects } = useExportState(["exportProjects", "loading"]);
const { getAllExports, removeExport } = useActions(["getAllExports", "removeExport"]);
//#endregion
const { showManageExport } = useExportState(["showManageExport"]);
const { setShowManageExport } = useActions(["setShowManageExport"]);
const navMenuItems = computed<NavMenuItem[]>(() => [
  { text: $t("webReportDesigner"), icon: "mdp-ListAndLabel", value: "web-report-designer" },
  { text: $t("desktopDesigner"), icon: "no-icon", value: "desktop-designer" }
]);

const handleMenuItem = (item: NavMenuItem) => {
  if (item == null || item?.value === "web-report-designer") {
    checkIfSavedExport();
  } else if (item?.value === "desktop-designer") {
    openWindowsDesigner();
  }
};
//#region ViewModel
type ViewModel = {
  selectedExportProject: ExportProjectModel | null;
  hasPreviousExport: boolean;
  showTranslationDialog: boolean;
  navMenuItems: NavMenuItem[];
};
const vm = reactive<ViewModel>({
  selectedExportProject: null,
  hasPreviousExport: false,
  showTranslationDialog: false,
  navMenuItems
});
//#endregion

const openManageExport = () => {
  setShowManageExport(true);
};

const isNew = computed(() => {
  return route.params.id == Guid.empty;
});

const refreshSelection = () => {
  if (!route.params.id) {
    return;
  }
  const project = exportProjects.value?.find((x) => x.exportId === route.params.id);
  if (project) {
    handleExportProjectSelection(project);
  } else if (route.name == "create-export" && route.params.id !== Guid.empty && exportProjects.value.length > 0) {
    createToast({
      text: $t("api.errors.InvalidExport"),
      title: $t("Error"),
      variant: "danger",
      titleIcon: "Export",
      textIllustration: "History",
      statusTextIcon: "XCircleSolid"
    });
    router.replace({ name: "create-export", params: { type: route.params.type, id: Guid.empty } });
  }
};

const exportType = computed<ExportType>(() => {
  return route.params.type as ExportType;
});

onMounted(async () => {
  await getAllExports({ type: exportType.value });
  vm.hasPreviousExport = await hasAnyLastExportRun(route.params.id);
});

watch(
  () => exportProjects,
  () => {
    refreshSelection();
  },
  { deep: true }
);

watch(
  () => route.params.id,
  async (nv) => {
    if (route.name == "create-export") {
      refreshSelection();
    }
    if (nv) {
      vm.hasPreviousExport = await hasAnyLastExportRun(nv);
    }
  }
);

//#region Handle Export Project Selection
const handleExportProjectSelection = (exportProject: ExportProjectModel) => {
  const options = exportProject?.options;
  const optionsObject = JSON.parse(options);
  if (exportProject.exportId !== route.params.id) {
    router.push({
      name: "create-export",
      params: { type: optionsObject?.ExportSettings?.ExportCriteria?.TargetType ?? route.params.type, id: exportProject.exportId }
    });
  } else {
    vm.selectedExportProject = exportProject;
  }
};
//#endregion

//#region Reset Export Data

function resetData() {
  emitter.emit("reset-export-data");
}

//#endregion
function checkIfSavedExport(e?: any) {
  if (route.params.id == Guid.empty) {
    if (confirm($t("exports.createExport.fastpage.designerRedirectionConfirmation"))) {
      e.preventDefault();
    }
  } else {
    router.push({ name: "fastpage-designer", params: { exportId: route.params.id } });
  }
}

function openWindowsDesigner(e?: any) {
  if (route.params.id == Guid.empty) {
    if (confirm($t("exports.createExport.fastpage.designerRedirectionConfirmation"))) {
      e.preventDefault();
    }
  } else {
    router.push({ name: "fastpage-desktop-designer", params: { exportId: route.params.id } });
  }
}

//#region Delete Export
const isDeleteDisabled = computed(() => {
  return vm.selectedExportProject == null || vm.selectedExportProject.exportId == null || vm.selectedExportProject.exportId == Guid.empty;
});

async function deleteExport() {
  if (vm.selectedExportProject == null) {
    return;
  }
  if (!confirm($t("exports.createExport.deleteExportConfirmation") as string)) {
    return;
  }
  await removeExport(vm.selectedExportProject?.exportId);
  emitter.emit("reset-export-after-delete");
  vm.selectedExportProject = null;
}

async function hasAnyLastExportRun(id: string) {
  return await exportApi.isLastExportEnabled(id);
}

async function goToLastExport() {
  try {
    const exportRunId = await exportApi.getLastExport(vm.selectedExportProject?.exportId);
    if (exportRunId !== Guid.empty) router.push({ name: "progress-export", params: { type: exportType.value, id: exportRunId } });
    else
      createToast({
        text: $t("api.errors.InvalidExportRun"),
        title: $t("Error"),
        variant: "danger",
        titleIcon: "Export",
        textIllustration: "History",
        statusTextIcon: "XCircleSolid"
      });
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      let errorMessage = "";
      if (error.response?.status === 409) {
        errorMessage = $t("api.errors.InvalidExport");
      } else {
        errorMessage = $te(`api.errors.${error?.response?.data.error}`) ? $t(`api.errors.${error?.response?.data.error}`) : error?.response?.data.error;
      }
      createToast({
        text: errorMessage,
        title: $t("Error"),
        variant: "danger",
        titleIcon: "Export",
        textIllustration: "History",
        statusTextIcon: "XCircleSolid"
      });
    }
  }
}

async function showTranslationModal() {
  vm.showTranslationDialog = true;
}

//#endregion
</script>
